import { AxiosError } from 'axios';
import { noop } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  intakeFormUpdateFieldOptions,
  intakeFormUpdateFieldValue,
} from '~/actions/intakeForm';
import { downloadTicketAttachment, uploadAtttachments } from '~/api/tickets';
import {
  customSettingsKeys,
  customSettingsOptionValues,
  valueKeys,
} from '~/common/constants/workflow';
import { documentTypeIcon } from '~/components/DocumentsPage/Document.utils';
import DepartmentSelect from '~/components/Shared/DepartmentSelect/DepartmentSelect';
import { showToast } from '~/components/Shared/EcToast/showToast';
import UserSelect from '~/components/Shared/UserSelect/UserSelect';
import { useAppConfigContext } from '~/contexts/AppConfig';
import { Box } from '~/eds/src/components/Box/Box';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { MarkedText } from '~/eds/src/components/MarkedText/MarkedText';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { Select } from '~/eds/src/components/Select/Select';
import { CountrySelect } from '~/eds/src/components/Select/subcomponents/CountrySelect';
import { CurrencySelect } from '~/eds/src/components/Select/subcomponents/CurrencySelect';
import { StateProvinceSelect } from '~/eds/src/components/Select/subcomponents/StateProvinceSelect';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { PilotId } from '~/eds/src/types/PilotId';
import { Uuid } from '~/eds/src/types/Uuid';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { TicketSpecialOptionValueType } from '~/enums/TicketSpecialOptionValueType';
import { TicketStageType } from '~/enums/TicketStageType';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { IntakeForm } from '~/redux/api/methods/ticket';
import * as selectors from '~/redux/selectors';
import { QuestionFieldState } from '~/redux/selectors';
import { IntakeFormFile } from '~/types/IntakeFormFile';
import { Nullable } from '~/types/Nullable';
import * as toastTypes from '~/types/toast.types';
import CheckboxGroup from '~/ui/components/CheckboxGroup/CheckboxGroup';
import DateInput from '~/ui/components/DateInput/DateInput';
import EmailInput from '~/ui/components/EmailInput/EmailInput';
import FileInput from '~/ui/components/FileInput/FileInput';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import UINumberInput from '~/ui/components/NumberInput/NumberInput';
import RadioGroup from '~/ui/components/RadioGroup/RadioGroup';
import TextInput from '~/ui/components/TextInput/TextInput';
import { downloadFile } from '~/utils/files';
import { createOnFilterOption } from '~/utils/select';

interface Props {
  id: Uuid;
  isEditing: boolean;
  search: string;
}

interface FieldProps {
  isPanelEnlarged: boolean;
  disabled: boolean;
  field: QuestionFieldState;
  intl: IntlShape;
  search: string;
  showError: boolean;
  validateAttachments: boolean;
  updateField: (id: Uuid, value: any) => void;
  intakeFormUpdateFieldOptions: (value: any) => void;
  setAttachments: React.Dispatch<
    React.SetStateAction<Nullable<IntakeFormFile[]>>
  >;
}

export const renderQuestionField = ({
  isPanelEnlarged,
  disabled,
  field,
  search,
  showError = false,
  updateField = noop,
  intakeFormUpdateFieldOptions: updateOptions = noop,
  setAttachments,
  validateAttachments = false,
  intl,
}: FieldProps) => {
  const {
    customSettings,
    customSettings: { format, options },
    errorMessage,
    id,
    type,
    value: fieldValue,
    wellKnownKey,
  } = field;
  const valueKey: any = valueKeys[type as keyof typeof valueKeys];

  function handleFileAttachError(
    error: AxiosError<{ data: string }>,
    fileName: string,
  ) {
    let message = intl.formatMessage({
      id: 'WorkflowQuestion.attachFileFailed',
      defaultMessage: 'Attach file failed. Please try again.',
      description: 'Error message when file attachment fails',
    });
    if (validateAttachments && error?.response?.status === 400) {
      message = intl.formatMessage(
        {
          id: 'WorkflowQuestion.fileUploadFailed',
          defaultMessage: '{fileName} failed to upload. {errorData}',
          description:
            'Error message when file upload fails with validation. Example: fileName = "document.pdf", errorData = "Invalid format"',
        },
        {
          fileName,
          errorData: error.response.data.data,
        },
      );
    }
    showToast(toastTypes.ERROR, message);
  }

  function downloadFileAttachment(fileObject: any) {
    downloadTicketAttachment(fileObject.id)
      .then((file) => {
        showToast(
          toastTypes.DOWNLOAD,
          intl.formatMessage(
            {
              id: 'WorkflowQuestion.downloadSuccess',
              defaultMessage: '{fileName} successfully downloaded.',
              description:
                'Success message when file download completes. Example: fileName = "document.pdf"',
            },
            {
              fileName: `${fileObject.fileName}.${fileObject.fileType}`,
            },
          ),
        );
        downloadFile(file, `${fileObject.fileName}.${fileObject.fileType}`);
      })
      .catch(() =>
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'WorkflowQuestion.downloadFailed',
            defaultMessage: 'Download attachment failed. Please try again.',
            description: 'Error message when file download fails',
          }),
        ),
      );
  }

  function getFieldValueByKey(key: string) {
    return fieldValue[key];
  }

  function setFieldValueByKey(key: string, newValue: any) {
    updateField(id, { ...fieldValue, [key]: newValue });
  }

  function setFieldOptions(options: any) {
    updateOptions({ id, options });
  }

  // TODO: Deprecate InputLabel after requirements for milestone are met.
  switch (type) {
    case WorkflowFieldType.Address: {
      // STREET_2 and STATE fields are non-required
      return (
        <Layout direction="column" spacing={2}>
          <InputLabel
            isRequired
            id="country"
            label={intl.formatMessage({
              id: 'WorkflowQuestion.selectCountry',
              defaultMessage: 'Select a Country',
              description: 'Label for country selection field',
            })}
            search={search}
          >
            <CountrySelect
              name="country-select"
              disabled={disabled}
              error={showError && errorMessage ? errorMessage : ''}
              value={getFieldValueByKey(valueKey.COUNTRY) || null}
              width={isPanelEnlarged ? 'input.l.width' : '100%'}
              onChange={(val: Nullable<string>) =>
                setFieldValueByKey(valueKey.COUNTRY, val)
              }
              isMulti={false}
              enablePortal={true}
            />
          </InputLabel>
          <InputLabel
            isRequired
            id="address"
            label={intl.formatMessage({
              id: 'WorkflowQuestion.addressLabel',
              defaultMessage: 'Address',
              description: 'Label for address input field',
            })}
            search={search}
          >
            <TextInput
              //   @ts-ignore props mapping missing
              disabled={disabled}
              error={!getFieldValueByKey(valueKey.STREET_1) && showError}
              placeholder={intl.formatMessage({
                id: 'WorkflowQuestion.streetAddress1Placeholder',
                defaultMessage: 'Street address 1',
                description: 'Placeholder for street address 1 field',
              })}
              value={getFieldValueByKey(valueKey.STREET_1)}
              width={isPanelEnlarged ? 'l' : 'fullWidth'}
              onChange={(val: string) =>
                setFieldValueByKey(valueKey.STREET_1, val)
              }
            />
            <TextInput
              //   @ts-ignore props mapping missing
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: 'WorkflowQuestion.streetAddress2Placeholder',
                defaultMessage: 'Street address 2',
                description: 'Placeholder for street address 2 field',
              })}
              value={getFieldValueByKey(valueKey.STREET_2)}
              width={isPanelEnlarged ? 'l' : 'fullWidth'}
              onChange={(val: string) =>
                setFieldValueByKey(valueKey.STREET_2, val)
              }
            />
          </InputLabel>
          <InputLabel
            isRequired
            id="city"
            label={intl.formatMessage({
              id: 'WorkflowQuestion.cityLabel',
              defaultMessage: 'Town / City',
              description: 'Label for city input field',
            })}
            search={search}
          >
            <TextInput
              //   @ts-ignore props mapping missing
              disabled={disabled}
              error={!getFieldValueByKey(valueKey.CITY) && showError}
              placeholder={intl.formatMessage({
                id: 'WorkflowQuestion.cityPlaceholder',
                defaultMessage: 'City',
                description: 'Placeholder for city field',
              })}
              value={getFieldValueByKey(valueKey.CITY)}
              width={isPanelEnlarged ? 'm' : 'fullWidth'}
              onChange={(val: string) => setFieldValueByKey(valueKey.CITY, val)}
            />
          </InputLabel>
          <InputLabel
            isRequired
            id="city"
            label={intl.formatMessage({
              id: 'WorkflowQuestion.stateProvinceLabel',
              defaultMessage: 'State / Province',
              description: 'Label for state/province selection field',
            })}
            search={search}
          >
            <StateProvinceSelect
              name="state-province-select"
              country={getFieldValueByKey(valueKey.COUNTRY)}
              //   @ts-ignore props mapping missing
              disabled={disabled}
              error={showError && errorMessage ? errorMessage : ''}
              value={getFieldValueByKey(valueKey.STATE) || null}
              width={isPanelEnlarged ? 'input.m.width' : '100%'}
              onChange={(val: Nullable<string>) =>
                setFieldValueByKey(valueKey.STATE, val)
              }
              isMulti={false}
              enablePortal={true}
            />
          </InputLabel>
          <InputLabel
            isRequired
            id="zipCode"
            label={intl.formatMessage({
              id: 'WorkflowQuestion.zipCodeLabel',
              defaultMessage: 'Zip Code / Post Code',
              description: 'Label for zip code field',
            })}
            search={search}
          >
            <TextInput
              //   @ts-ignore props mapping missing
              disabled={disabled}
              error={!getFieldValueByKey(valueKey.ZIP_CODE) && showError}
              placeholder={
                getFieldValueByKey(valueKey.COUNTRY) === 'United States'
                  ? intl.formatMessage({
                      id: 'WorkflowQuestion.zipCodePlaceholder',
                      defaultMessage: 'Zip Code',
                      description: 'Placeholder for US zip code',
                    })
                  : intl.formatMessage({
                      id: 'WorkflowQuestion.postCodePlaceholder',
                      defaultMessage: 'Post Code',
                      description: 'Placeholder for non-US postal code',
                    })
              }
              value={getFieldValueByKey(valueKey.ZIP_CODE)}
              width={isPanelEnlarged ? 's' : 'fullWidth'}
              onChange={(val: string) =>
                setFieldValueByKey(valueKey.ZIP_CODE, val)
              }
            />
          </InputLabel>
        </Layout>
      );
    }
    case WorkflowFieldType.Boolean: {
      const BOOLEAN_OPTIONS = [
        {
          label: intl.formatMessage({
            id: 'WorkflowQuestion.yesOption',
            defaultMessage: 'Yes',
            description: 'Yes option for boolean fields',
          }),
          value: true,
        },
        {
          label: intl.formatMessage({
            id: 'WorkflowQuestion.noOption',
            defaultMessage: 'No',
            description: 'No option for boolean fields',
          }),
          value: false,
        },
      ];
      return (
        <Select
          name="boolean-select"
          disabled={disabled}
          error={showError && errorMessage ? errorMessage : ''}
          options={BOOLEAN_OPTIONS}
          value={getFieldValueByKey(valueKey.VALUE) || null}
          onChange={(val) => setFieldValueByKey(valueKey.VALUE, val)}
          isMulti={false}
          enablePortal={true}
        />
      );
    }
    case WorkflowFieldType.Checkbox: {
      return (
        <Layout flexDirection="column" spacing={4}>
          <CheckboxGroup
            disabled={disabled}
            options={options || []}
            values={getFieldValueByKey(valueKey.VALUE) || []}
            onChange={(val: string[]) =>
              setFieldValueByKey(valueKey.VALUE, val)
            }
          />
          {getFieldValueByKey(valueKey.VALUE)?.includes(
            TicketSpecialOptionValueType.Other,
          ) && (
            <TextInput
              //   @ts-ignore props mapping missing
              disabled={disabled}
              placeholder={intl.formatMessage({
                id: 'WorkflowQuestion.specifyOtherPlaceholder',
                defaultMessage: 'Specify other option...',
                description: 'Placeholder for other option text input',
              })}
              value={getFieldValueByKey(valueKey.OTHER_VALUE)}
              width="m"
              onChange={(val: string) =>
                setFieldValueByKey(valueKey.OTHER_VALUE, val)
              }
            />
          )}
        </Layout>
      );
    }
    case WorkflowFieldType.Date: {
      return (
        <DateInput
          //   @ts-ignore props mapping missing
          disabled={disabled}
          error={showError}
          format={format}
          value={getFieldValueByKey(valueKey.VALUE)}
          width={isPanelEnlarged ? 'm' : 'fullWidth'}
          onChange={(val: string) => setFieldValueByKey(valueKey.VALUE, val)}
        />
      );
    }
    case WorkflowFieldType.Department: {
      const { departments } = customSettings;
      const blacklistOptions =
        customSettings[customSettingsKeys[type].RULE] ===
        customSettingsOptionValues[type].RULE.ALL_EXCEPT
          ? departments
          : [];
      const whitelistOptions =
        customSettings[customSettingsKeys[type].RULE] ===
        customSettingsOptionValues[type].RULE.SPECIFIC
          ? departments
          : [];

      return customSettings[customSettingsKeys[type].ALLOW_MULTIPLE] ? (
        <DepartmentSelect
          notAvailableDepartments={blacklistOptions}
          disabled={disabled}
          isMulti
          error={showError && errorMessage ? errorMessage : ''}
          value={getFieldValueByKey(valueKey.VALUE) || []}
          specificDepartments={whitelistOptions}
          width={isPanelEnlarged ? 'l' : '100%'}
          onChange={(val: number[]) => setFieldValueByKey(valueKey.VALUE, val)}
          enablePortal={true}
        />
      ) : (
        <DepartmentSelect
          notAvailableDepartments={blacklistOptions}
          disabled={disabled}
          error={showError && errorMessage ? errorMessage : ''}
          value={getFieldValueByKey(valueKey.VALUE) || null}
          specificDepartments={whitelistOptions}
          width={isPanelEnlarged ? 'l' : '100%'}
          onChange={(val: number) => setFieldValueByKey(valueKey.VALUE, val)}
          enablePortal={true}
        />
      );
    }
    case WorkflowFieldType.Email: {
      return (
        <EmailInput
          //   @ts-ignore props mapping missing
          disabled={disabled}
          error={showError}
          value={getFieldValueByKey(valueKey.VALUE)}
          width={isPanelEnlarged ? 'm' : 'fullWidth'}
          onChange={(val: string) => setFieldValueByKey(valueKey.VALUE, val)}
        />
      );
    }
    case WorkflowFieldType.File: {
      const allFileTypes = Object.values(FileExtensionType);
      const allowedFileTypes =
        customSettings[customSettingsKeys[type].ALLOWED_FILE_TYPES];
      const acceptedFileTypes = allFileTypes
        .filter((type) => !allowedFileTypes?.includes(type))
        .concat(
          allowedFileTypes?.filter((t: any) => !allowedFileTypes?.includes(t)),
        )
        .toString();
      const allowMultipleFiles =
        customSettings[customSettingsKeys[type].FILE_LIMIT] !==
        customSettingsOptionValues[type].FILE_LIMIT.SINGLE;
      const customSettingsAllowedTypes =
        customSettings[customSettingsKeys[type].ALLOWED_FILE_TYPE_RULE];
      const fileTypes =
        customSettingsAllowedTypes ===
        customSettingsOptionValues[type].FILE_TYPE_RULE.ANY_EXCEPT
          ? acceptedFileTypes
          : customSettings[customSettingsKeys[type].ALLOWED_FILE_TYPES]
            ? customSettings[
                customSettingsKeys[type].ALLOWED_FILE_TYPES
              ].toString()
            : '*';

      const handleFileDataChange = (newFile: IntakeFormFile[]) => {
        setAttachments((prevState) => {
          const newAttachments = [...(prevState ? prevState : []), ...newFile];
          return newAttachments.length ? newAttachments : null;
        });
      };

      return (
        <Layout direction="column" spacing={4}>
          {fieldValue?.value?.map((fileObject: any) => {
            return (
              <Layout
                align="center"
                bg="gray-200"
                key={fileObject.id}
                justifyContent="space-between"
                mb={3}
                px={3}
                spacing={6}
                sx={{ borderRadius: 'm', height: 'input-height' }}
              >
                <Layout
                  align="center"
                  onClick={() => downloadFileAttachment(fileObject)}
                >
                  {documentTypeIcon(fileObject?.fileType)}
                  <Text variant="body-bold" color="inverse.text.link">
                    {fileObject.fileName}
                  </Text>
                </Layout>
                <IconButton
                  disabled={disabled}
                  icon="trash"
                  size="s"
                  tooltip={intl.formatMessage({
                    id: 'WorkflowQuestion.removeFileTooltip',
                    defaultMessage: 'remove file',
                    description: 'Tooltip for remove file button',
                  })}
                  onClick={() => {
                    const newFiles = fieldValue.value.filter(
                      (f: any) => f.id !== fileObject.id,
                    );
                    setAttachments(newFiles);
                  }}
                />
              </Layout>
            );
          })}
          {fieldValue?.value?.length && !allowMultipleFiles ? null : (
            <FileInput
              // @ts-ignore props mapping missing
              error={showError && errorMessage ? errorMessage : ''}
              acceptedFiles={fileTypes}
              enableUpload={!disabled}
              enableDropzone
              enableMultiple={allowMultipleFiles}
              onChange={(files) =>
                uploadAtttachments(
                  files,
                  handleFileDataChange,
                  handleFileAttachError,
                  validateAttachments ? field.id : undefined,
                )
              }
              label={undefined}
              file={undefined}
              id={undefined}
            />
          )}
        </Layout>
      );
    }
    case WorkflowFieldType.LongResponse: {
      return (
        <TextArea
          readOnly={disabled}
          error={showError && errorMessage ? errorMessage : ''}
          name="Long Response"
          value={getFieldValueByKey(valueKey.VALUE)}
          width="100%"
          onChange={(value: Nullable<string>) =>
            setFieldValueByKey(valueKey.VALUE, value)
          }
        />
      );
    }
    case WorkflowFieldType.MonetaryValue: {
      const currencyRule =
        customSettings[customSettingsKeys[type].CURRENCY_RULE];
      const currencyList =
        customSettings[customSettingsKeys[type].CURRENCY_LIST];
      return (
        <Layout direction="column" spacing={2}>
          <CurrencySelect
            name="currency-select"
            width={isPanelEnlarged ? 'input.l.width' : '100%'}
            onFilterOption={createOnFilterOption({
              includeValues:
                currencyRule ===
                  customSettingsOptionValues[type].CURRENCY_RULE.SPECIFIC &&
                currencyList,
              excludeValues:
                currencyRule ===
                  customSettingsOptionValues[type].CURRENCY_RULE.ANY_EXCEPT &&
                currencyList,
            })}
            disabled={disabled}
            error={showError && errorMessage ? errorMessage : ''}
            value={getFieldValueByKey(valueKey.CURRENCY) || null}
            onChange={(val) => setFieldValueByKey(valueKey.CURRENCY, val)}
            isMulti={false}
            enablePortal={true}
          />
          <NumberInput
            disabled={disabled}
            error={showError && errorMessage ? errorMessage : ''}
            name="Monetary Value"
            value={getFieldValueByKey(valueKey.AMOUNT)}
            width={isPanelEnlarged ? 'input.l.width' : '100%'}
            onChange={(val: Nullable<number>) =>
              setFieldValueByKey(valueKey.AMOUNT, val)
            }
          />
        </Layout>
      );
    }
    case WorkflowFieldType.MultiSelect: {
      const handleAddOption = (newOption: any) => {
        setFieldOptions([...options, newOption]);
      };
      return (
        <Select
          name="multi-select"
          disabled={disabled}
          error={showError && errorMessage ? errorMessage : ''}
          limit={100}
          isMulti
          options={options || []}
          placeholder={intl.formatMessage({
            id: 'WorkflowQuestion.pickOptionPlaceholder',
            defaultMessage: 'Pick an option...',
            description: 'Placeholder for multi-select dropdown',
          })}
          value={getFieldValueByKey(valueKey.VALUE) || null}
          width={isPanelEnlarged ? 'input.l.width' : '100%'}
          onAddOption={
            customSettings[customSettingsKeys[type].ENABLE_CREATE_OPTIONS]
              ? handleAddOption
              : undefined
          }
          onChange={(val: Nullable<string[]>) =>
            setFieldValueByKey(valueKey.VALUE, val)
          }
          enablePortal={true}
        />
      );
    }
    case WorkflowFieldType.Number: {
      const { allow_decimals } = customSettings;
      // TODO: Update NumberInput to handle decimals.
      return (
        <UINumberInput
          //   @ts-ignore props mapping missing
          disabled={disabled}
          error={showError}
          value={getFieldValueByKey(valueKey.VALUE)}
          width={isPanelEnlarged ? 's' : 'fullWidth'}
          onChange={(val: Nullable<number>) =>
            setFieldValueByKey(valueKey.VALUE, val)
          }
          allowDecimals={!!allow_decimals}
        />
      );
    }
    case WorkflowFieldType.Percentage: {
      const { min, max } = customSettings;
      return (
        <NumberInput
          disabled={disabled}
          error={showError && errorMessage ? errorMessage : ''}
          max={max}
          min={min}
          name="Monetary Value"
          value={getFieldValueByKey(valueKey.VALUE)}
          width={isPanelEnlarged ? 'input.s.width' : '100%'}
          onChange={(val: Nullable<number>) =>
            setFieldValueByKey(valueKey.VALUE, val)
          }
        />
      );
    }
    case WorkflowFieldType.Radio: {
      return (
        <Layout flexDirection="column" space={4}>
          <RadioGroup
            disabled={disabled}
            options={options || []}
            value={getFieldValueByKey(valueKey.VALUE)}
            onChange={(val) => setFieldValueByKey(valueKey.VALUE, val)}
          />
          {getFieldValueByKey(valueKey.VALUE) ===
            TicketSpecialOptionValueType.Other && (
            <Box mt={4}>
              <TextInput //   @ts-ignore props mapping missing
                disabled={disabled}
                placeholder={intl.formatMessage({
                  id: 'WorkflowQuestion.specifyOtherRadioPlaceholder',
                  defaultMessage: 'Specify other option...',
                  description:
                    'Placeholder for other option text input in radio',
                })}
                value={getFieldValueByKey(valueKey.OTHER_VALUE)}
                width="m"
                onChange={(val: string) =>
                  setFieldValueByKey(valueKey.OTHER_VALUE, val)
                }
              />
            </Box>
          )}
        </Layout>
      );
    }
    case WorkflowFieldType.ShortResponse: {
      if (disabled) {
        return (
          <Text preset="description">
            {getFieldValueByKey(valueKey.VALUE) ||
              intl.formatMessage({
                id: 'WorkflowQuestion.enterValuePlaceholder',
                defaultMessage: 'Enter a value...',
                description: 'Placeholder for empty short response field',
              })}
          </Text>
        );
      }
      if (wellKnownKey === 'contract_description') {
        return (
          <TextArea
            readOnly={disabled}
            error={showError && errorMessage ? errorMessage : ''}
            name="contract_description"
            value={getFieldValueByKey(valueKey.VALUE)}
            width="100%"
            onChange={(value: Nullable<string>) =>
              setFieldValueByKey(valueKey.VALUE, value)
            }
          />
        );
      }
      return (
        <TextInput
          //   @ts-ignore props mapping missing
          disabled={disabled}
          error={showError}
          value={getFieldValueByKey(valueKey.VALUE)}
          width={isPanelEnlarged ? 'l' : 'fullWidth'}
          onChange={(val: string) => setFieldValueByKey(valueKey.VALUE, val)}
        />
      );
    }
    case WorkflowFieldType.SingleSelect: {
      const handleAddOption = (newOption: any) => {
        setFieldOptions([...options, newOption]);
      };
      if (disabled) {
        return (
          <Text preset="description">
            {getFieldValueByKey(valueKey.VALUE) ||
              intl.formatMessage({
                id: 'WorkflowQuestion.selectOptionPlaceholder',
                defaultMessage: 'Select an option...',
                description: 'Placeholder for empty single select field',
              })}
          </Text>
        );
      }
      return (
        <Select
          name="single-select"
          disabled={disabled}
          limit={100}
          error={showError && errorMessage ? errorMessage : ''}
          options={options || []}
          value={getFieldValueByKey(valueKey.VALUE) || null}
          width={isPanelEnlarged ? 'input.l.width' : '100%'}
          onAddOption={
            customSettings[customSettingsKeys[type].ENABLE_CREATE_OPTIONS]
              ? handleAddOption
              : undefined
          }
          onChange={(val: string) => setFieldValueByKey(valueKey.VALUE, val)}
          isMulti={false}
          enablePortal={true}
        />
      );
    }
    case WorkflowFieldType.TimePeriod: {
      return (
        <Layout direction="column" spacing={2}>
          <NumberInput
            disabled={disabled}
            error={showError && errorMessage ? errorMessage : ''}
            name="Time Period"
            value={getFieldValueByKey(valueKey.AMOUNT)}
            width={isPanelEnlarged ? 'input.s.width' : '100%'}
            onChange={(val: Nullable<number>) =>
              setFieldValueByKey(valueKey.AMOUNT, val)
            }
          />
          <Select
            name="time-period"
            disabled={disabled}
            error={showError && errorMessage ? errorMessage : ''}
            options={options || []}
            value={getFieldValueByKey(valueKey.PERIOD) || null}
            width={isPanelEnlarged ? 'input.m.width' : '100%'}
            onChange={(val) => setFieldValueByKey(valueKey.PERIOD, val)}
            isMulti={false}
            enablePortal={true}
          />
        </Layout>
      );
    }
    case WorkflowFieldType.User: {
      const { users } = customSettings;
      if (customSettings[customSettingsKeys[type].ALLOW_MULTIPLE]) {
        return (
          <UserSelect
            name="user-select"
            notAvailableUsers={
              customSettings[customSettingsKeys[type].RULE] ===
              customSettingsOptionValues[type].RULE.ALL_EXCEPT
                ? users
                : undefined
            }
            disabled={disabled}
            error={showError && errorMessage ? errorMessage : ''}
            isMulti
            placeholder={intl.formatMessage({
              id: 'WorkflowQuestion.specifyUsersPlaceholder',
              defaultMessage: 'Specify one or more users…',
              description: 'Placeholder for multi-user selection',
            })}
            value={getFieldValueByKey(valueKey.VALUE) || null}
            specificUsers={
              customSettings[customSettingsKeys[type].RULE] ===
              customSettingsOptionValues[type].RULE.SPECIFIC
                ? users
                : undefined
            }
            width={isPanelEnlarged ? 'input.l.width' : '100%'}
            onChange={(val: Nullable<PilotId[] | null>) =>
              setFieldValueByKey(valueKey.VALUE, val)
            }
            enablePortal={true}
          />
        );
      }
      return (
        <UserSelect
          name="user-select"
          notAvailableUsers={
            customSettings[customSettingsKeys[type].RULE] ===
            customSettingsOptionValues[type].RULE.ALL_EXCEPT
              ? users
              : undefined
          }
          disabled={disabled}
          error={showError && errorMessage ? errorMessage : ''}
          isMulti={false}
          placeholder={intl.formatMessage({
            id: 'WorkflowQuestion.specifyUserPlaceholder',
            defaultMessage: 'Specify a user…',
            description: 'Placeholder for single user selection',
          })}
          value={getFieldValueByKey(valueKey.VALUE) || null}
          specificUsers={
            customSettings[customSettingsKeys[type].RULE] ===
            customSettingsOptionValues[type].RULE.SPECIFIC
              ? users
              : undefined
          }
          width={isPanelEnlarged ? 'input.l.width' : '100%'}
          onChange={(val: Nullable<PilotId>) =>
            setFieldValueByKey(valueKey.VALUE, val)
          }
          enablePortal={true}
        />
      );
    }
    default:
      return null;
  }
};

export const WorkflowQuestion = ({ id, isEditing, search }: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const { isPanelEnlarged } = useAppConfigContext();
  const {
    description,
    isRequired,
    title,
    isReadOnly: isQuestionReadOnly,
  } = useSelector((intakeForm: IntakeForm) =>
    selectors.selectFormQuestion(intakeForm, id),
  );
  const field = useSelector((intakeForm: IntakeForm) =>
    selectors.selectQuestionField(intakeForm, id),
  );
  const [attachments, setAttachments] = useState<Nullable<IntakeFormFile[]>>(
    field.type === WorkflowFieldType.File ? field.value.value : null,
  );
  const isFormValidated = useSelector(selectors.selectFormValidation);
  const ticketStage = useSelector(selectors.selectTicketStage);
  const {
    customSettings,
    errorMessage,
    id: fieldId,
    isEnabled,
    isPlaceHolder,
    type,
    warningMessage,
  } = field;
  const showError = !!(isFormValidated && errorMessage);
  const updateField = (id: Uuid, value: any) =>
    dispatch(intakeFormUpdateFieldValue({ id, value: value }));

  const isCheckFormFieldFileExtensionEnabled = useFlag(
    FlagType.CheckFormFieldFileExtension,
  );

  const isIntakeFormFieldWithReadOnlyEnabled = useFlag(
    FlagType.IntakeFormFieldWithReadOnly,
  );

  const isReadOnly = isIntakeFormFieldWithReadOnlyEnabled
    ? isQuestionReadOnly
    : false;

  // populate email when email_type is submitter
  useEffect(() => {
    const isSubmitterType =
      customSettings?.[
        customSettingsKeys[WorkflowFieldType.Email].EMAIL_TYPE
      ] ===
      customSettingsOptionValues?.[WorkflowFieldType.Email].EMAIL_TYPE
        .SUBMITTER;
    if (type === WorkflowFieldType.Email && isSubmitterType) {
      updateField(fieldId, { value: currentUser.email });
    }
  }, []);

  useEffect(() => {
    if (attachments && field && field.id) {
      updateField(field.id, { ...field.value, value: attachments });
    }
  }, [attachments]);

  useEffect(() => {
    if (field && field.type === WorkflowFieldType.File && field.value) {
      setAttachments(field.value.value);
    }
  }, [field.value]);

  if (
    !isEnabled ||
    [WorkflowFieldType.DateSigned, WorkflowFieldType.Signature].includes(type)
  ) {
    return null;
  }

  const isDisabled =
    (isPlaceHolder &&
      [TicketStageType.Sign, TicketStageType.Finalize].includes(ticketStage)) ||
    !isEditing;

  const questionComponent = renderQuestionField({
    isPanelEnlarged: isPanelEnlarged,
    disabled: isDisabled || isReadOnly,
    field,
    search,
    showError,
    updateField,
    intakeFormUpdateFieldOptions,
    setAttachments,
    validateAttachments: isCheckFormFieldFileExtensionEnabled,
    intl,
  });

  return (
    <Layout
      direction="column"
      spacing={2}
      data-testid={`intake-form-question-${title.toLowerCase().replace(/ /g, '-')}`}
    >
      <MarkedText search={search} text={isRequired ? `${title} *` : title} />
      {description && (
        <Text preset="description" whiteSpace="pre-wrap">
          <MarkedText search={search} text={description} />
        </Text>
      )}
      <Layout direction="column" spacing={2}>
        {questionComponent && (
          <Tooltip
            tooltip={
              isReadOnly &&
              intl.formatMessage({
                id: 'WorkflowQuestion.syncedFieldTooltip',
                defaultMessage:
                  'This is a synced field and cannot be edited in Evisort',
                description: 'Tooltip for read-only synced fields',
              })
            }
          >
            {questionComponent}
          </Tooltip>
        )}
        {showError && <Text color="status.danger">{errorMessage}</Text>}
        {
          // do not show the warningMessage when creating a ticket but need to show it afterwards (refer to EKP-2186)
          !!ticketStage && warningMessage && <Text>{warningMessage}</Text>
        }
      </Layout>
    </Layout>
  );
};
